/**
 * Sorting helper functions for dropdown options and lists
 */

/**
 * Sort options alphabetically with "Other" always at the end
 *
 * @param {Array} options - Array of option objects with 'label' property
 * @param {Object} config - Configuration object
 * @param {boolean} config.isNumeric - Whether to sort numerically (default: auto-detect)
 * @param {string} config.labelKey - Key to use for label (default: 'label')
 * @param {string} config.otherLabel - Label to identify as "other" (default: 'other')
 * @returns {Array} Sorted array of options
 *
 * @example
 * // Basic usage with auto-detection
 * const options = [
 *   { label: 'Red', id: 1 },
 *   { label: 'Blue', id: 2 },
 *   { label: 'Other', id: 3 },
 *   { label: 'Green', id: 4 }
 * ];
 * const sorted = sortOptionsAlphabetically(options);
 * // Result: [Blue, Green, Red, Other]
 *
 * @example
 * // Numeric sorting
 * const sizes = [
 *   { label: '10', id: 1 },
 *   { label: '2', id: 2 },
 *   { label: '5', id: 3 }
 * ];
 * const sorted = sortOptionsAlphabetically(sizes, { isNumeric: true });
 * // Result: [2, 5, 10]
 *
 * @example
 * // Custom label key
 * const items = [
 *   { name: 'Zebra', id: 1 },
 *   { name: 'Apple', id: 2 },
 *   { name: 'Other', id: 3 }
 * ];
 * const sorted = sortOptionsAlphabetically(items, { labelKey: 'name' });
 * // Result: [Apple, Zebra, Other]
 */
export const sortOptionsAlphabetically = (options, config = {}) => {
  const {
    isNumeric = null,
    labelKey = 'label',
    otherLabel = 'other',
  } = config;

  if (!Array.isArray(options) || options.length === 0) {
    return options;
  }

  // Auto-detect if sorting should be numeric
  const shouldSortNumeric = isNumeric !== null
    ? isNumeric
    : options.some(opt => {
        const label = opt[labelKey];
        return label && !isNaN(label);
      });

  return [...options].sort((a, b) => {
    const aLabel = a[labelKey];
    const bLabel = b[labelKey];

    // Handle null/undefined labels
    if (!aLabel || !bLabel) return 0;

    // Check if either option is "Other" (case-insensitive)
    const aIsOther = String(aLabel).toLowerCase().trim() === otherLabel.toLowerCase();
    const bIsOther = String(bLabel).toLowerCase().trim() === otherLabel.toLowerCase();

    // If one is "Other", move it to the end
    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;

    // Sort numerically if applicable
    if (shouldSortNumeric) {
      return Number(aLabel) - Number(bLabel);
    }

    // Sort alphabetically (case-insensitive)
    return String(aLabel).localeCompare(String(bLabel), undefined, {
      sensitivity: 'base'
    });
  });
};

/**
 * Sort function to be used directly in Array.sort()
 * Useful for inline sorting without creating a new array
 *
 * @param {Object} config - Configuration object (same as sortOptionsAlphabetically)
 * @returns {Function} Comparator function for Array.sort()
 *
 * @example
 * // Inline sorting
 * const options = [
 *   { label: 'Red', id: 1 },
 *   { label: 'Other', id: 2 },
 *   { label: 'Blue', id: 3 }
 * ];
 * options.sort(createSortComparator());
 * // options is now: [Blue, Red, Other]
 *
 * @example
 * // With custom config
 * const items = [
 *   { name: '10', id: 1 },
 *   { name: '2', id: 2 },
 *   { name: 'Other', id: 3 }
 * ];
 * items.sort(createSortComparator({ labelKey: 'name', isNumeric: true }));
 * // items is now: [2, 10, Other]
 */
export const createSortComparator = (config = {}) => {
  const {
    isNumeric = null,
    labelKey = 'label',
    otherLabel = 'other',
  } = config;

  return (a, b) => {
    const aLabel = a[labelKey];
    const bLabel = b[labelKey];

    // Handle null/undefined labels
    if (!aLabel || !bLabel) return 0;

    // Check if either option is "Other" (case-insensitive)
    const aIsOther = String(aLabel).toLowerCase().trim() === otherLabel.toLowerCase();
    const bIsOther = String(bLabel).toLowerCase().trim() === otherLabel.toLowerCase();

    // If one is "Other", move it to the end
    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;

    // Auto-detect numeric if not specified
    const shouldSortNumeric = isNumeric !== null
      ? isNumeric
      : !isNaN(aLabel) && !isNaN(bLabel);

    // Sort numerically if applicable
    if (shouldSortNumeric) {
      return Number(aLabel) - Number(bLabel);
    }

    // Sort alphabetically (case-insensitive)
    return String(aLabel).localeCompare(String(bLabel), undefined, {
      sensitivity: 'base'
    });
  };
};

/**
 * Legacy inline sort function for backward compatibility
 * This matches the exact sorting logic used in the original code
 *
 * @param {Array} data - Array of options to sort
 * @param {boolean} sortType - Whether to sort numerically
 * @returns {Array} Sorted array
 *
 * @deprecated Use sortOptionsAlphabetically or createSortComparator instead
 */
export const sortDropdownOptions = (data, sortType = false) => {
  if (!Array.isArray(data)) return data;

  return [...data].sort((a, b) => {
    if (!a.label || !b.label) return 0;

    const aIsOther = a.label.toLowerCase().trim() === 'other';
    const bIsOther = b.label.toLowerCase().trim() === 'other';

    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;

    if (sortType) {
      return Number(a.label) - Number(b.label);
    }
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
};
